import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { v4 } from 'uuid';

import { AnswerTypes } from 'types';

import Loader from 'components/Loader';
import { FirstScreen, QuestionScreen, Divider, LoadScreen, Email, Sales } from 'components/screens';

import getQuizData from 'api/getQuizData';
import useWindowHeight from 'hooks/useWindowHeight';
import { useAuth } from '../contexts/AuthContext';
import trackEvent from '../utils/trackEvent';

const Swicher = () => {
    const [activeScreen, setActiveScreen] = useState(0);
    const [quizData, setQuzData] = useState<Record<any, any>>();
    const { windowHeight } = useWindowHeight();
    const location = useLocation();
    const { quizId } = useParams();
    const params = new URLSearchParams(location.search);
    const quizScreenFromUrl = Number(params.get('screen'));
    const history = useNavigate();
    const { user } = useAuth();

    const localQuizId = sessionStorage.getItem('quizId');

    useEffect(() => {
        const userId = localStorage.getItem('userId');

        if ((quizId && !localQuizId && quizId) || (quizId && localQuizId !== quizId)) {
            sessionStorage.setItem('quizId', quizId);
        }
        if (!userId) {
            const newUserId = v4();

            localStorage.setItem('userId', newUserId);
        }
        if (userId) {
            // amplitude.getInstance().init('636ce5c8ecdee17ba7740758fd5d77cd', userId);
            // amplitude.getInstance().setUserId(userId);
        }
        // Funnel + referral attribution: quiz started.
        if (quizId) trackEvent('quiz_started', { quizId });
    }, [quizId, localQuizId]);

    useEffect(() => {
        if (!quizData && localQuizId) {
            getQuizData(localQuizId).then((res) => {
                setQuzData(res?.[0]?.data);
            });
        }
    }, [localQuizId, quizData]);

    const changeUrlQuizScreen = (screenNumber: number): void => {
        params.set('screen', screenNumber.toString());

        history(`${location.pathname}?${params.toString()}`);
    };

    const handleSwitchToNextScreen = useCallback(() => {
        changeUrlQuizScreen(activeScreen + 1);
    }, [activeScreen]);

    const handleSwitchToPrevScreen = useCallback(() => {
        if (activeScreen) {
            changeUrlQuizScreen(activeScreen - 1);
        }
    }, [activeScreen]);

    useLayoutEffect(() => {
        if (activeScreen === 0 && !quizScreenFromUrl) {
            changeUrlQuizScreen(0);
        }
    }, [activeScreen, quizScreenFromUrl]);

    useEffect(() => {
        // Validate screen number before setting
        if (quizData?.screens) {
            const maxScreen = quizData.screens.length - 1;
            
            // If screen number exceeds quiz length, quiz is completed
            if (quizScreenFromUrl > maxScreen) {
                console.log('Quiz completed!');
                
                // Check if this is a user_profile quiz
                const quizType = quizData?.quiz_type || quizData?.data?.quiz_type;
                console.log('Quiz type:', quizType);
                
                if (quizType === 'user_profile') {
                    // Submit quiz results for user_profile type
                    const userEmail = user?.email || localStorage.getItem('userEmail');
                    const userAnswers = sessionStorage.getItem('userAnswers');
                    
                    console.log('📝 Submitting user_profile quiz...');
                    console.log('📧 Email:', userEmail);
                    console.log('📊 Answers:', userAnswers);
                    
                    if (userEmail && userAnswers) {
                        fetch('/api/submit-quiz', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                email: userEmail,
                                answers: JSON.parse(userAnswers),
                                quest_name: localQuizId,
                            }),
                        })
                        .then(res => res.json())
                        .then(result => {
                            console.log('✅ Quiz submitted:', result);
                            if (result.profile_built) {
                                console.log('🎉 Redirecting to dashboard...');
                                setTimeout(() => {
                                    window.location.href = '/dashboard';
                                }, 500);
                            }
                        })
                        .catch(err => {
                            console.error('❌ Error submitting quiz:', err);
                            history(`/thank-page/${localQuizId}`);
                        });
                    } else {
                        console.error('❌ Missing email or answers');
                        history(`/thank-page/${localQuizId}`);
                    }
                } else {
                    // Regular quiz - redirect to thank page
                    console.log('Regular quiz, redirecting to thank page');
                    history(`/thank-page/${localQuizId}`);
                }
                return;
            }
            
            const validScreen = Math.min(Math.max(0, quizScreenFromUrl || 0), maxScreen);
            setActiveScreen(validScreen);
        } else {
            setActiveScreen(quizScreenFromUrl || 0);
        }
    }, [quizScreenFromUrl, quizData, history, localQuizId]);

    useEffect(() => {
        document.documentElement.style.setProperty('--main-bg-color', quizData?.bgColor);
        document.documentElement.style.setProperty('--them-color', quizData?.themeColor);
    }, [quizData]);

    const lengthOnboarding = useMemo(() => {
        if (quizData?.screens) {
            // @ts-ignore
            const findIndex = Object.values(quizData.screens).findIndex((item) => item?.type === 'loadScreen');

            return findIndex - 1;
        }
        return 0;
    }, [quizData]);

    const screenProps: AnswerTypes = useMemo(
        () => ({
            id: quizData?.screens?.[activeScreen]?.id,
            title: quizData?.screens?.[activeScreen]?.title,
            text: quizData?.screens?.[activeScreen]?.text,
            buttonText: quizData?.screens?.[activeScreen]?.buttonText,
            image: quizData?.screens?.[activeScreen]?.image,
            eventName: quizData?.screens?.[activeScreen]?.eventName,
            logoUrl: quizData?.logoUrl,
            termsLink: quizData?.screens?.[activeScreen]?.termsLink,
            policyLink: quizData?.screens?.[activeScreen]?.policyLink,
            subscriptionLink: quizData?.screens?.[activeScreen]?.subscriptionLink,
            answers: quizData?.screens?.[activeScreen]?.answers,
            question: quizData?.screens?.[activeScreen]?.question,
            reviews: quizData?.screens?.[activeScreen]?.reviews,
            billingsData: quizData?.screens?.[activeScreen]?.billingsData,
            logoLink: quizData?.screens?.[activeScreen]?.logoLink,
            discountModalData: quizData?.screens?.[activeScreen]?.discountModalData,
            lengthOnboarding,
            handleSwitchToNextScreen,
            handleSwitchToPrevScreen,
        }),
        [quizData, activeScreen],
    );

    const Screens: Record<string, ReactNode> = {
        firstScreen: <FirstScreen {...screenProps} />,
        question: <QuestionScreen {...screenProps} />,
        divider: <Divider {...screenProps} />,
        loadScreen: <LoadScreen {...screenProps} />,
        email: <Email {...screenProps} />,
        sales: <Sales {...screenProps} />,
    };

    if (!quizData) return <Loader />;
    
    // Safety check: ensure screen exists
    const currentScreen = quizData?.screens?.[activeScreen];
    if (!currentScreen) {
        console.error('Screen not found:', activeScreen, 'Total screens:', quizData?.screens?.length);
        return <Loader />;
    }

    return (
        <main className="wrapper" style={{ height: windowHeight }}>
            <Helmet>
                <link rel="apple-touch-icon" href={quizData?.logo512} />
                <link rel="icon" href={quizData?.favicon} />
                <title>{quizData.siteTitle}</title>
            </Helmet>
            {Screens[currentScreen.type]}
        </main>
    );
};

export default Swicher;
